/**
 * File: useDateRangeSelectController.ts
 * Path: apps/admin/src/shared/hooks/useDateRangeSelectController.ts
 * Description: State and rules of the Overview's date range picker: open or
 * closed, the draft dates, the first tap of a range and the month on show.
 */
import { useMemo, useState } from "react";
import { PRESETS, type DateRange, type PresetId } from "../utils/overviewFiguresUtils";
import { comparisonText, monthShift, rangeProblem, sameMonth } from "../utils/dateRangeUtils";

interface Options {
  range: DateRange;
  today: string;
  booksStart: string;
  presetRange: (id: PresetId) => DateRange;
  onChange: (range: DateRange) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const useDateRangeSelectController = ({ range, today, booksStart, presetRange, onChange, open: controlledOpen, onOpenChange }: Options) => {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const setOpen = (next: boolean) => {
    setInnerOpen(next);
    onOpenChange?.(next);
  };
  const [draft, setDraft] = useState<DateRange>(range);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [month, setMonth] = useState(sameMonth(range.to));

  const presets = useMemo(() => new Map(PRESETS.map((p) => [p.id, presetRange(p.id)])), [presetRange]);
  const activePreset = PRESETS.find((p) => {
    const r = presets.get(p.id);
    return r && r.from === draft.from && r.to === draft.to;
  })?.id;

  const problem = rangeProblem(draft, today, booksStart);

  const reset = (next: DateRange) => {
    setDraft(next);
    setAnchor(null);
    setMonth(sameMonth(next.to));
  };

  const pickDay = (day: string) => {
    if (anchor === null) {
      setDraft({ from: day, to: day });
      setAnchor(day);
    } else {
      setDraft(day < anchor ? { from: day, to: anchor } : { from: anchor, to: day });
      setAnchor(null);
    }
  };

  const setField = (key: keyof DateRange, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setAnchor(null);
    if (value) setMonth(sameMonth(value));
  };

  return {
    open,
    draft,
    month,
    activePreset,
    problem,
    comparison: problem === null ? comparisonText(draft) : null,
    canGoBack: monthShift(month, -1) >= sameMonth(booksStart),
    canGoForward: monthShift(month, 1) <= sameMonth(today),
    goBack: () => setMonth(monthShift(month, -1)),
    goForward: () => setMonth(monthShift(month, 1)),
    onOpenChange: (next: boolean) => {
      if (next) reset(range);
      setOpen(next);
    },
    cancel: () => setOpen(false),
    apply: () => {
      onChange(draft);
      setOpen(false);
    },
    choosePreset: (id: PresetId) => reset(presets.get(id) ?? range),
    chooseCustom: () => setAnchor(null),
    pickDay,
    setField,
  };
};
